import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gauge, Settings2, Loader2, UserPlus } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Slider } from "./ui/slider";
import { api } from "../lib/api";

const BANDS = [
  ["low_green", "أخضر منخفض", "#65a30d"],
  ["mid_green", "أخضر متوسط", "#16a34a"],
  ["high_green", "أخضر مرتفع", "#166534"],
  ["platinum", "بلاتيني", "#64748b"],
];
// Saudis (full, wage ≥ 4000) needed so (S + x) / (N + x) ≥ p
const need = (S, N, p) => Math.max(0, Math.ceil(((p / 100) * N - S) / (1 - p / 100) - 1e-9));

const Settings = ({ cfg, onSaved }) => {
  const [f, setF] = useState({ activity: cfg.activity || "", thresholds: { ...(cfg.thresholds || {}) }, expected: { ...(cfg.expected || {}) } });
  const [busy, setBusy] = useState(false);
  const save = async () => { setBusy(true); try { onSaved(await api.saveNitaqat(f)); toast.success("تم حفظ نسب نطاقات"); } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحفظ"); } finally { setBusy(false); } };
  return (
    <div className="grid gap-2 rounded-xl border border-slate-200 bg-slate-50 p-3 sm:grid-cols-2 lg:grid-cols-6" data-testid="nitaqat-settings">
      <label className="space-y-1 text-xs font-semibold text-slate-600 sm:col-span-2">النشاط
        <Input data-testid="nitaqat-activity" value={f.activity} onChange={(e) => setF((p) => ({ ...p, activity: e.target.value }))} placeholder="مثال: خدمات التوصيل" className="bg-white text-sm font-normal" />
      </label>
      {[["thresholds", "الحالي"], ["expected", "المتوقع"]].map(([grp, gl]) => BANDS.map(([k, l], i) => (
        <label key={grp + k} className={`space-y-1 text-xs font-semibold text-slate-600 ${i === 0 ? "lg:col-start-3" : ""}`}>{l} ({gl}) %
          <Input data-testid={`nitaqat-${grp === "thresholds" ? "th" : "ex"}-${k}`} type="number" step="0.01" dir="ltr" value={f[grp][k] ?? ""} onChange={(e) => setF((p) => ({ ...p, [grp]: { ...p[grp], [k]: e.target.value } }))} className="bg-white text-sm font-normal" />
        </label>
      )))}
      <Button data-testid="nitaqat-save" onClick={save} disabled={busy} className="sm:col-span-2 lg:col-span-6">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "حفظ النسب"}</Button>
    </div>
  );
};

export const NitaqatSimulator = ({ stats, canEdit }) => {
  const [cfg, setCfg] = useState(null);
  const [edit, setEdit] = useState(false);
  const [hire, setHire] = useState(0);
  const [mode, setMode] = useState("thresholds");
  useEffect(() => { api.nitaqat().then(setCfg).catch(() => setCfg({ thresholds: {} })); }, []);
  if (!cfg) return null;
  const hasExp = Object.keys(cfg.expected || {}).length > 0;
  const th = (mode === "expected" && hasExp ? cfg.expected : cfg.thresholds) || {};
  const bands = BANDS.filter(([k]) => th[k]);
  const S = stats.weighted_saudi ?? stats.active_saudi, N = stats.active;
  const pctOf = (x) => (N + x ? ((S + x) / (N + x)) * 100 : 0);
  const bandOf = (p) => [...bands].reverse().find(([k]) => p >= th[k]) || ["red", "أحمر", "#ef4444"];
  const cur = pctOf(0), sim = pctOf(hire), cb = bandOf(cur), sb = bandOf(sim);
  const max = Math.max(...bands.map(([k]) => th[k]), 1) * 1.15;
  return (
    <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4" data-testid="nitaqat-simulator">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-heading font-bold"><Gauge className="h-5 w-5 text-emerald-600" /> محاكي نطاقات {cfg.activity && <span className="text-xs font-normal text-slate-500">— {cfg.activity}</span>}</h3>
        <div className="flex flex-wrap gap-2">
        {hasExp && <div className="flex rounded-lg bg-slate-100 p-0.5 text-xs font-bold" data-testid="nitaqat-mode">
          {[["thresholds", "الحالي"], ["expected", "المتوقع"]].map(([m, l]) => <button key={m} data-testid={`nitaqat-mode-${m}`} onClick={() => setMode(m)} className={`rounded-md px-3 py-1.5 transition-colors ${mode === m ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>{l}</button>)}
        </div>}
        {canEdit && <Button size="sm" variant="outline" data-testid="nitaqat-edit" onClick={() => setEdit((v) => !v)} className="gap-1.5"><Settings2 className="h-4 w-4" /> نسب النطاقات</Button>}
        </div>
      </div>
      {edit && <Settings cfg={cfg} onSaved={(d) => { setCfg(d); setEdit(false); }} />}
      {!bands.length ? (
        <p className="rounded-xl border border-dashed border-amber-300 bg-amber-50 p-4 text-sm text-amber-800" data-testid="nitaqat-empty">أدخل نسب نطاقات نشاطك من زر «نسب النطاقات» (تجدها في منصة قوى) ليعمل المحاكي.</p>
      ) : (<>
        <div className="flex flex-wrap items-end gap-4">
          <div><p className="text-xs text-slate-500">النطاق الحالي</p><p className="font-heading text-2xl font-extrabold" style={{ color: cb[2] }} data-testid="nitaqat-current-band">{cb[1]}</p></div>
          <div><p className="text-xs text-slate-500">نسبة التوطين الموزونة</p><p className="font-heading text-2xl font-extrabold" dir="ltr" data-testid="nitaqat-current-pct">{cur.toFixed(2)}%</p></div>
          <p className="text-xs text-slate-500">{S} سعودي محتسب من {N} موظف نشط{stats.half_count ? ` · ${stats.half_count} بأجر أقل من 4000 يُحسبون نصفاً` : ""}</p>
        </div>
        <div className="relative h-4 overflow-hidden rounded-full bg-red-100" data-testid="nitaqat-bar">
          <div className="absolute inset-y-0 right-0 rounded-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${Math.min(100, (sim / max) * 100)}%`, background: sb[2] }} />
          {bands.map(([k, l]) => <div key={k} title={`${l} ${th[k]}%`} className="absolute inset-y-0 w-0.5 bg-slate-900/60" style={{ right: `${(th[k] / max) * 100}%` }} />)}
        </div>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {bands.map(([k, l, c]) => { const n = need(S, N, th[k]); return (
            <div key={k} data-testid={`nitaqat-need-${k}`} className="rounded-xl border px-3 py-2" style={{ borderColor: c }}>
              <p className="text-xs font-bold" style={{ color: c }}>{l} · {th[k]}%</p>
              <p className="text-sm font-semibold">{n === 0 ? "محقق ✓" : <>تحتاج <b className="text-lg">{n}</b> سعودي</>}</p>
            </div>); })}
        </div>
        <div className="space-y-2 rounded-xl bg-slate-50 p-3">
          <p className="flex items-center gap-2 text-sm font-semibold"><UserPlus className="h-4 w-4" /> لو وظّفت <b data-testid="nitaqat-hire-count">{hire}</b> سعودي (بأجر 4000 فأكثر):
            <span style={{ color: sb[2] }} className="font-bold" data-testid="nitaqat-sim-result">{sim.toFixed(2)}% — {sb[1]}</span></p>
          <Slider data-testid="nitaqat-hire-slider" value={[hire]} min={0} max={Math.max(10, ...bands.map(([k]) => need(S, N, th[k]) + 5))} step={1} onValueChange={([v]) => setHire(v)} />
        </div>
        <p className="text-[11px] text-slate-400">تقدير استرشادي — المرجع الرسمي منصة قوى (لا يشمل احتساب ذوي الإعاقة أو الطلاب أو المتوسط المتحرك).</p>
      </>)}
    </div>
  );
};
